import React from "react";
import { Zap } from "lucide-react";
import { CARD } from "@/styles/shared";
import { timeAgo } from "@/utils/timeAgo"; // eslint-disable-line no-unused-vars

const DASH_CARD = {
  ...CARD,
  background: "rgba(255,255,255,0.82)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.7)",
};

export default function FocusSection({ tasks, wipCount, projects, onOpenKanban }) {
  const handleOpen = (task) => {
    if (!task.project_id || !onOpenKanban) return;
    const project = projects.find((p) => p.id === task.project_id);
    if (project) onOpenKanban(project);
  };

  return (
    <div style={{ ...DASH_CARD, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div style={{
        padding: "0.875rem 1.25rem",
        borderBottom: "1px solid rgba(26,77,46,0.1)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Zap size={16} color="#3d6b1f" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.95rem", fontWeight: 700 }}>
            Your Focus
          </span>
        </div>
        <span style={{
          fontFamily: "'Manrope', sans-serif", fontSize: "0.72rem", fontWeight: 600,
          color: wipCount >= 3 ? "#dc2626" : "#6b7280",
          background: wipCount >= 3 ? "rgba(220,38,38,0.1)" : "rgba(26,77,46,0.07)",
          border: `1px solid ${wipCount >= 3 ? "rgba(220,38,38,0.25)" : "rgba(26,77,46,0.15)"}`,
          borderRadius: "2rem", padding: "0.2rem 0.6rem",
        }}>
          {wipCount}/3 WIP
        </span>
      </div>

      <div style={{ padding: "0.75rem", display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
        {tasks.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", textAlign: "center" }}>
            <div>
              <div style={{ fontSize: "1.75rem", marginBottom: "0.4rem" }}>✓</div>
              <p style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.82rem", margin: 0 }}>
                Nothing in progress — pick a task!
              </p>
            </div>
          </div>
        ) : (
          tasks.map((task) => (
            <FocusTaskCard key={task.id} task={task} onOpen={() => handleOpen(task)} />
          ))
        )}
      </div>
    </div>
  );
}

function FocusTaskCard({ task, onOpen }) {
  const hasBadge = task.is_overdue || task.is_stale;
  return (
    <div
      data-testid={`focus-task-${task.id}`}
      onClick={onOpen}
      style={{
        background: "rgba(255,255,255,0.6)",
        border: task.is_overdue
          ? "1px solid rgba(220,38,38,0.35)"
          : task.is_stale
          ? "1px solid rgba(217,119,6,0.35)"
          : "1px solid rgba(26,77,46,0.12)",
        borderRadius: "0.625rem",
        padding: "0.625rem 0.875rem",
        cursor: task.project_id ? "pointer" : "default",
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => { if (task.project_id) e.currentTarget.style.background = "rgba(255,255,255,0.88)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.6)"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
        <p style={{
          fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.85rem",
          fontWeight: 600, margin: 0, flex: 1,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {task.title}
        </p>
        {hasBadge && (
          <span style={{
            fontFamily: "'Manrope', sans-serif", fontSize: "0.62rem", fontWeight: 700,
            background: task.is_overdue ? "rgba(220,38,38,0.1)" : "rgba(217,119,6,0.1)",
            color: task.is_overdue ? "#dc2626" : "#d97706",
            border: `1px solid ${task.is_overdue ? "rgba(220,38,38,0.3)" : "rgba(217,119,6,0.3)"}`,
            borderRadius: "0.3rem", padding: "0.1rem 0.4rem",
            flexShrink: 0, textTransform: "uppercase", letterSpacing: "0.04em",
          }}>
            {task.is_overdue ? "OVERDUE" : `STALE · ${task.days_in_progress}d`}
          </span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.3rem", flexWrap: "wrap" }}>
        {task.project_emoji && task.project_title && (
          <span style={{
            fontFamily: "'Manrope', sans-serif", fontSize: "0.7rem", color: "#3d6b1f",
            background: "rgba(61,107,31,0.08)", border: "1px solid rgba(61,107,31,0.18)",
            borderRadius: "0.3rem", padding: "0.05rem 0.35rem", fontWeight: 600,
          }}>
            {task.project_emoji} {task.project_title}
          </span>
        )}
        <span style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.7rem" }}>
          {task.days_in_progress > 0 ? `${task.days_in_progress}d in progress` : "Started today"}
        </span>
      </div>
    </div>
  );
}
